import type { ComponentType } from 'react'

import { bindWindows, createWindowRegistry, createWindowShell, type WindowShell } from '@krp/client-runtime'
import { DUPLICATES_WINDOWS } from '@krp/duplicates-ui'
import { PLATFORM_WINDOWS } from '@krp/platform-ui'
import { AQSI_WINDOWS } from '@krp/resource-aqsi-ui'
import { OWN_WINDOWS } from '@krp/resource-own-ui'
import { CASHIER_ACCESS_WINDOWS, CASHIER_ANALYTICS_WINDOWS, CASHIER_GROUPS_WINDOWS, OWN_CASHIERS_WINDOWS, OWN_JOURNAL_WINDOWS, SALES_POINTS_WINDOWS } from '@krp/resource-receipts-ui'
import { SCHEDULE_WINDOWS } from '@krp/schedule-ui'
import { SYNC_WINDOWS } from '@krp/sync-ui'
import {
  ONEC_CREATE_DEFAULTS_WINDOWS,
  ONEC_IMPORT_WINDOWS,
  ONEC_INVENTORY_WINDOWS,
  ONEC_PRICE_TAG_WINDOWS,
  ONEC_PRICING_WINDOWS,
  ONEC_RECEIPT_WINDOWS,
  ONEC_SHIFT_WINDOWS,
  ONEC_STRUCTURE_WINDOWS,
  ONEC_SUPPLY_WINDOWS,
  ONEC_WINDOWS,
} from '@krp/resource-onec-ui'

import type { RootState } from '../app/types'
import { CashierLinksWindowBinding } from '../bindings/receipts/CashierLinksWindowBinding'
import { CashiersWindowBinding } from '../bindings/receipts/CashiersWindowBinding'
import { OwnCashierCardBinding } from '../bindings/receipts/OwnCashierCardBinding'
import { OwnCashiersListBinding } from '../bindings/receipts/OwnCashiersListBinding'
import { CashierSetCodeWindowBinding } from '../bindings/receipts/CashierSetCodeWindowBinding'
import { CashierStatisticsBinding } from '../bindings/receipts/CashierStatisticsBinding'
import { OwnReceiptDetailBinding } from '../bindings/receipts/OwnReceiptDetailBinding'
import { SalesPointEditBinding, SalesPointStatisticsBinding, SalesPointsBinding } from '../bindings/receipts/SalesPointsBinding'
import { AboutBinding } from '../bindings/platform/AboutBinding'
import { MaintenanceBinding } from '../bindings/platform/MaintenanceBinding'
import { PhotoSettingsBinding } from '../bindings/platform/PhotoSettingsBinding'
import { HardwareBinding } from '../bindings/platform/HardwareBinding'
import { AccessBinding } from '../bindings/platform/AccessBinding'
import { DiagnosticsBinding } from '../bindings/platform/DiagnosticsBinding'
import { ResourceCardBinding } from '../bindings/platform/ResourceCardBinding'
import { ResourceEditorBinding } from '../bindings/platform/ResourceEditorBinding'
import { DeliveryBinding } from '../bindings/aqsi/DeliveryBinding'
import { ReceiptDetailBinding } from '../bindings/aqsi/ReceiptDetailBinding'
import { ReceiptsBinding, RefundSearchBinding, SalesStatisticsBinding, ReceiptAttentionBinding, ParkedSalesBinding } from '../bindings/aqsi/ReceiptsBinding'
import { AqsiShiftsBinding } from '../bindings/aqsi/ShiftsBinding'
import { ImportFileBinding } from '../bindings/onec/ImportFileBinding'
import { ImportProfilesBinding } from '../bindings/onec/ImportProfilesBinding'
import { ImportWizardBinding } from '../bindings/onec/ImportWizardBinding'
import { InventoryBinding } from '../bindings/onec/InventoryBinding'
import { OneCProductCardBinding } from '../bindings/onec/OneCProductCardBinding'
import { OneCProductCreateBinding } from '../bindings/onec/OneCProductCreateBinding'
import { OneCProductsBinding } from '../bindings/onec/OneCProductsBinding'
import { PriceTagsBinding } from '../bindings/onec/PriceTagsBinding'
import { StructureBinding } from '../bindings/onec/StructureBinding'
import { CreateDefaultsBinding } from '../bindings/onec/CreateDefaultsBinding'
import { CashReceiptsBinding, CashSalesStatisticsBinding } from '../bindings/onec/CashReceiptsBinding'
import { CashReceiptDetailBinding } from '../bindings/onec/CashReceiptDetailBinding'
import { CashShiftsBinding } from '../bindings/onec/CashShiftsBinding'
import { PriceListBinding } from '../bindings/onec/PriceListBinding'
import { PriceTypesBinding } from '../bindings/onec/PriceTypesBinding'
import { PricingBinding } from '../bindings/onec/PricingBinding'
import { SuppliesBinding } from '../bindings/onec/SuppliesBinding'
import { SupplyBinding } from '../bindings/onec/SupplyBinding'
import { SupplyCreateBinding } from '../bindings/onec/SupplyCreateBinding'
import { SupplyReceiptsBinding } from '../bindings/onec/SupplyReceiptsBinding'
import { ResourcesBinding } from '../bindings/platform/ResourcesBinding'
import { SourceAddBinding } from '../bindings/platform/SourceAddBinding'
import { AqsiCatalogBinding } from '../bindings/aqsi/AqsiCatalogBinding'
import { AqsiGoodBinding } from '../bindings/aqsi/AqsiGoodBinding'
import { PriceKindsScreen } from '@krp/resource-aqsi-ui'
import { AqsiStructureBinding } from '../bindings/aqsi/AqsiStructureBinding'
import { AqsiPrintSettingsBinding } from '../bindings/aqsi/AqsiPrintSettingsBinding'
import { AqsiCreateDefaultsBinding } from '../bindings/aqsi/AqsiCreateDefaultsBinding'
import { AqsiTraceBinding } from '../bindings/aqsi/AqsiTraceBinding'
import { AqsiRmkBinding } from '../bindings/aqsi/AqsiRmkBinding'
import { AqsiSaleResolveBinding, AqsiSaleScannerBinding } from '../bindings/aqsi/AqsiSaleBinding'
import {
  AqsiDiscrepanciesBinding,
  AqsiIncidentsBinding,
  AqsiRecoveryProofBinding,
  AqsiRefundBinding,
  AqsiRefundLookupBinding,
  AqsiSlipRefundBinding,
} from '../bindings/aqsi/AqsiRefundsBinding'
import {
  AqsiCatalogSourceBinding,
} from '../bindings/aqsi/AqsiSourceBinding'
import { RmkSourcesBinding } from '../bindings/platform/RmkSourcesBinding'
import { OwnGroupsBinding } from '../bindings/own/OwnGroupsBinding'
import { OwnPricesBinding } from '../bindings/own/OwnPricesBinding'
import { OwnProductCardBinding } from '../bindings/own/OwnProductCardBinding'
import { OwnProductPricesBinding } from '../bindings/own/OwnProductPricesBinding'
import { OwnProductsBinding } from '../bindings/own/OwnProductsBinding'
import { OwnCashiersBinding } from '../bindings/own/OwnCashiersBinding'
import { OwnDeliveryBinding } from '../bindings/own/OwnDeliveryBinding'
import { OwnSaleBinding } from '../bindings/own/OwnSaleBinding'
import { PhotoScannerBinding } from '../bindings/platform/PhotoScannerBinding'
import { PhotoSearchBinding } from '../bindings/platform/PhotoSearchBinding'
import { CatalogLinksBinding } from '../bindings/platform/CatalogLinksBinding'
import { DuplicatesSourceBinding } from '../bindings/duplicates/DuplicatesSourceBinding'
import { ScheduleCashiersBinding } from '../bindings/schedule/ScheduleCashiersBinding'
import { ScheduleHomeBinding } from '../bindings/schedule/ScheduleHomeBinding'
import { SchedulePlaceBinding } from '../bindings/schedule/SchedulePlaceBinding'
import { ScheduleEditPlaceBinding, ScheduleNewPlaceBinding } from '../bindings/schedule/SchedulePlaceFormBinding'
import { SyncDivergencesBinding } from '../bindings/sync/SyncDivergencesBinding'
import { SyncHomeBinding } from '../bindings/sync/SyncHomeBinding'
import { SyncIgnoresBinding } from '../bindings/sync/SyncIgnoresBinding'
import { SyncInitBinding } from '../bindings/sync/SyncInitBinding'
import { SyncJobBinding } from '../bindings/sync/SyncJobBinding'
import { SyncJournalBinding } from '../bindings/sync/SyncJournalBinding'
import { SyncPriceMapsBinding } from '../bindings/sync/SyncPriceMapsBinding'
import { SyncScheduleBinding } from '../bindings/sync/SyncScheduleBinding'
import { NomenclatureSettingsBinding } from '../bindings/platform/NomenclatureSettingsBinding'
import { CommonCatalogBinding } from '../bindings/platform/CommonCatalogBinding'
import { CommonProductBinding } from '../bindings/platform/CommonProductBinding'
import { PhotoAttachBinding } from '../bindings/platform/PhotoAttachBinding'
import { ProductPickBinding } from '../bindings/platform/ProductPickBinding'
import { SECTION_LANDING_BINDINGS } from '../bindings/platform/SectionLandingBinding'
import {
  TestingCheckBinding,
  TestingHomeBinding,
  TestingRunBinding,
  TestingRunsBinding,
  TestProductBinding,
  TestProductsBinding,
  TextProductBinding,
  TextProductsBinding,
} from '../bindings/platform/TestingBindings'
import { TextCheckBinding, TextQueriesBinding, TextRunBinding, TextRunsBinding } from '../bindings/platform/TextTestingBindings'
import { TwinBinding } from '../bindings/platform/TwinTestingBindings'
import { aqsiModule, duplicatesModule, oneCModule, ownModule, platformModule, receiptsModule, scheduleModule, syncModule } from './modules'

/**
 * Реестр окон приложения: дескрипторы всех пакетов + binding на каждое окно.
 * Маршруты, меню, шапка и вкладки собираются из него (R11). Окно без binding
 * или binding без окна — ошибка при сборке (createWindowShell).
 */
export const WINDOW_REGISTRY = createWindowRegistry<RootState>([
  ...bindWindows(platformModule.id, (state: RootState) => state.platform, platformModule.windows),
  ...bindWindows(oneCModule.id, (state: RootState) => state.onec, oneCModule.windows),
  ...bindWindows(aqsiModule.id, (state: RootState) => state.aqsi, aqsiModule.windows),
  ...bindWindows(ownModule.id, (state: RootState) => state.own, ownModule.windows),
  ...bindWindows(receiptsModule.id, (state: RootState) => state.receipts, receiptsModule.windows),
  ...bindWindows(duplicatesModule.id, (state: RootState) => state.duplicates, duplicatesModule.windows),
  ...bindWindows(syncModule.id, (state: RootState) => state.sync, syncModule.windows),
  ...bindWindows(scheduleModule.id, (state: RootState) => state.schedule, scheduleModule.windows),
])

/** Экран на каждое окно: ключ — kind окна из дескриптора пакета, значение — привязка к store приложения (`bindings/*`). */
export const WINDOW_BINDINGS: Readonly<Record<string, ComponentType>> = {
  [PLATFORM_WINDOWS.resources]: ResourcesBinding,
  [PLATFORM_WINDOWS.resource]: ResourceCardBinding,
  [PLATFORM_WINDOWS.sourceAdd]: SourceAddBinding,
  [PLATFORM_WINDOWS.resourceAdd]: ResourceEditorBinding,
  [PLATFORM_WINDOWS.resourceEdit]: ResourceEditorBinding,
  [PLATFORM_WINDOWS.access]: AccessBinding,
  [PLATFORM_WINDOWS.diagnostics]: DiagnosticsBinding,
  [PLATFORM_WINDOWS.testing]: TestingHomeBinding,
  [PLATFORM_WINDOWS.testingProducts]: TestProductsBinding,
  [PLATFORM_WINDOWS.testingProduct]: TestProductBinding,
  [PLATFORM_WINDOWS.testingCheck]: TestingCheckBinding,
  [PLATFORM_WINDOWS.testingRuns]: TestingRunsBinding,
  [PLATFORM_WINDOWS.testingRun]: TestingRunBinding,
  [PLATFORM_WINDOWS.testingTextQueries]: TextQueriesBinding,
  [PLATFORM_WINDOWS.testingTextProducts]: TextProductsBinding,
  [PLATFORM_WINDOWS.testingTextProduct]: TextProductBinding,
  [PLATFORM_WINDOWS.testingTextCheck]: TextCheckBinding,
  [PLATFORM_WINDOWS.testingTextQueryCheck]: TextCheckBinding,
  [PLATFORM_WINDOWS.testingTextRuns]: TextRunsBinding,
  [PLATFORM_WINDOWS.testingTextRun]: TextRunBinding,
  [PLATFORM_WINDOWS.testingTwin]: TwinBinding,
  [ONEC_WINDOWS.products]: OneCProductsBinding,
  [ONEC_WINDOWS.product]: OneCProductCardBinding,
  [ONEC_WINDOWS.productCreate]: OneCProductCreateBinding,
  [ONEC_PRICING_WINDOWS.priceList]: PriceListBinding,
  [ONEC_PRICING_WINDOWS.priceTypes]: PriceTypesBinding,
  [ONEC_PRICING_WINDOWS.overview]: PricingBinding,
  [ONEC_IMPORT_WINDOWS.wizard]: ImportWizardBinding,
  [ONEC_IMPORT_WINDOWS.profiles]: ImportProfilesBinding,
  [ONEC_IMPORT_WINDOWS.file]: ImportFileBinding,
  [ONEC_SUPPLY_WINDOWS.list]: SuppliesBinding,
  [ONEC_SUPPLY_WINDOWS.create]: SupplyCreateBinding,
  // То же окно создания, но с файлом накладной из адреса (B-2 приёмки U9).
  [ONEC_SUPPLY_WINDOWS.fromInvoice]: SupplyCreateBinding,
  [ONEC_SUPPLY_WINDOWS.supply]: SupplyBinding,
  [ONEC_SUPPLY_WINDOWS.receipts]: SupplyReceiptsBinding,
  [ONEC_INVENTORY_WINDOWS.inventory]: InventoryBinding,
  [ONEC_PRICE_TAG_WINDOWS.priceTags]: PriceTagsBinding,
  [ONEC_STRUCTURE_WINDOWS.structure]: StructureBinding,
  [ONEC_RECEIPT_WINDOWS.receipts]: CashReceiptsBinding,
  [ONEC_RECEIPT_WINDOWS.salesStatistics]: CashSalesStatisticsBinding,
  [ONEC_RECEIPT_WINDOWS.receipt]: CashReceiptDetailBinding,
  [ONEC_SHIFT_WINDOWS.shifts]: CashShiftsBinding,
  // RP-SYN11: «Создание по умолчанию» базы 1С — из карточки ресурса.
  [ONEC_CREATE_DEFAULTS_WINDOWS.createDefaults]: CreateDefaultsBinding,
  // Окно «РМК» источника «Собственная»: общий экран продажи и оборудование этого компьютера (bindings/own).
  [OWN_WINDOWS.sale]: OwnSaleBinding,
  // RP-OWN3: своя номенклатура и цены — вкладки «Номенклатуры» и «Цен», карточка, новый товар, группы, цены товара.
  [OWN_WINDOWS.products]: OwnProductsBinding,
  [OWN_WINDOWS.product]: OwnProductCardBinding,
  [OWN_WINDOWS.productCreate]: OwnProductCardBinding,
  [OWN_WINDOWS.groups]: OwnGroupsBinding,
  [OWN_WINDOWS.prices]: OwnPricesBinding,
  [OWN_WINDOWS.productPrices]: OwnProductPricesBinding,
  // RP-OWN9: доставка в центральный журнал — локальный отчёт агента и ведомость сервера.
  [OWN_WINDOWS.delivery]: OwnDeliveryBinding,
  [OWN_WINDOWS.cashiers]: OwnCashiersBinding,
  [AQSI_WINDOWS.catalogSource]: AqsiCatalogSourceBinding,
  [AQSI_WINDOWS.rmk]: AqsiRmkBinding,
  [AQSI_WINDOWS.printSettings]: AqsiPrintSettingsBinding,
  [AQSI_WINDOWS.structure]: AqsiStructureBinding,
  [AQSI_WINDOWS.trace]: AqsiTraceBinding,
  [AQSI_WINDOWS.prices]: PriceKindsScreen,
  [AQSI_WINDOWS.catalog]: AqsiCatalogBinding,
  [AQSI_WINDOWS.good]: AqsiGoodBinding,
  [AQSI_WINDOWS.receipts]: ReceiptsBinding,
  [AQSI_WINDOWS.salesStatistics]: SalesStatisticsBinding,
  [AQSI_WINDOWS.shifts]: AqsiShiftsBinding,
  [AQSI_WINDOWS.receiptAttention]: ReceiptAttentionBinding,
  [AQSI_WINDOWS.parkedSales]: ParkedSalesBinding,
  [AQSI_WINDOWS.receipt]: ReceiptDetailBinding,
  [AQSI_WINDOWS.delivery]: DeliveryBinding,
  [AQSI_WINDOWS.createDefaults]: AqsiCreateDefaultsBinding,
  [AQSI_WINDOWS.saleScanner]: AqsiSaleScannerBinding,
  [AQSI_WINDOWS.saleResolve]: AqsiSaleResolveBinding,
  [AQSI_WINDOWS.refund]: AqsiRefundBinding,
  [AQSI_WINDOWS.refundLookup]: AqsiRefundLookupBinding,
  [AQSI_WINDOWS.refundSearch]: RefundSearchBinding,
  [AQSI_WINDOWS.slipRefund]: AqsiSlipRefundBinding,
  [AQSI_WINDOWS.incidents]: AqsiIncidentsBinding,
  [AQSI_WINDOWS.recoveryProof]: AqsiRecoveryProofBinding,
  [AQSI_WINDOWS.discrepancies]: AqsiDiscrepanciesBinding,
  [PLATFORM_WINDOWS.catalogLinks]: CatalogLinksBinding,
  // RP-FB50: общий товар — одно окно и для правки, и для создания; настройки обмена и конфликты приёма.
  [PLATFORM_WINDOWS.commonProduct]: CommonProductBinding,
  [PLATFORM_WINDOWS.commonProductNew]: CommonProductBinding,
  // RP-FB56: сканирование как пополнение базы — выбор товара общего списка и подтверждение «установить это фото?».
  [PLATFORM_WINDOWS.productPick]: ProductPickBinding,
  [PLATFORM_WINDOWS.photoAttach]: PhotoAttachBinding,
  [PLATFORM_WINDOWS.search]: PhotoSearchBinding,
  [PLATFORM_WINDOWS.scanner]: PhotoScannerBinding,
  [PLATFORM_WINDOWS.about]: AboutBinding,
  [PLATFORM_WINDOWS.maintenance]: MaintenanceBinding,
  // RP-CACHE-0: настройки раздела «Фото» (сроки и потолок кэша фото).
  [PLATFORM_WINDOWS.photoSettings]: PhotoSettingsBinding,
  [PLATFORM_WINDOWS.hardware]: HardwareBinding,
  // UI-SECTIONS: входы разделов. «Номенклатура» показывает общий каталог, остальные открывают вкладку ресурса.
  [PLATFORM_WINDOWS.nomenclature]: CommonCatalogBinding,
  [PLATFORM_WINDOWS.nomenclatureSettings]: NomenclatureSettingsBinding,
  [OWN_JOURNAL_WINDOWS.detail]: OwnReceiptDetailBinding,
  [CASHIER_ANALYTICS_WINDOWS.statistics]: CashierStatisticsBinding,
  [CASHIER_GROUPS_WINDOWS.report]: CashierLinksWindowBinding,
  [OWN_CASHIERS_WINDOWS.list]: OwnCashiersListBinding,
  [OWN_CASHIERS_WINDOWS.card]: OwnCashierCardBinding,
  [CASHIER_ACCESS_WINDOWS.list]: CashiersWindowBinding,
  [SALES_POINTS_WINDOWS.list]: SalesPointsBinding,
  [SALES_POINTS_WINDOWS.statistics]: SalesPointStatisticsBinding,
  [SALES_POINTS_WINDOWS.edit]: SalesPointEditBinding,
  [CASHIER_ACCESS_WINDOWS.setCode]: CashierSetCodeWindowBinding,
  [DUPLICATES_WINDOWS.source]: DuplicatesSourceBinding,
  // RP-SYN8: синхронизация v2 — расхождения источников и ручное применение (ADR-23).
  [SYNC_WINDOWS.home]: SyncHomeBinding,
  [SYNC_WINDOWS.divergences]: SyncDivergencesBinding,
  [SYNC_WINDOWS.job]: SyncJobBinding,
  [SYNC_WINDOWS.init]: SyncInitBinding,
  [SYNC_WINDOWS.ignores]: SyncIgnoresBinding,
  [SYNC_WINDOWS.priceMaps]: SyncPriceMapsBinding,
  [SYNC_WINDOWS.schedule]: SyncScheduleBinding,
  [SYNC_WINDOWS.journal]: SyncJournalBinding,
  // График выхода кассиров: плитки объектов, календарь месяца объекта, окно дня, форма объекта, копирование недели.
  [SCHEDULE_WINDOWS.home]: ScheduleHomeBinding,
  [SCHEDULE_WINDOWS.place]: SchedulePlaceBinding,
  [SCHEDULE_WINDOWS.cashiers]: ScheduleCashiersBinding,
  [SCHEDULE_WINDOWS.newPlace]: ScheduleNewPlaceBinding,
  [SCHEDULE_WINDOWS.editPlace]: ScheduleEditPlaceBinding,
  ...SECTION_LANDING_BINDINGS,
  // UI-RMK-FLOW 3.4: вход «РМК» — плитки источников, а не автозамена первой вкладкой.
  [PLATFORM_WINDOWS.rmk]: RmkSourcesBinding,
}

/** Оболочка окон приложения: реестр дескрипторов, срез `windows` корня состояния и привязки экранов. Создаётся один раз в `main.tsx`. */
export function createAppWindowShell(): WindowShell {
  return createWindowShell<RootState>({
    registry: WINDOW_REGISTRY,
    selectWindows: (state) => state.windows,
    renderers: WINDOW_BINDINGS,
  })
}
